import { mapDateStringToNgbDateStruct, mapNgbDateStructToDateString } from '@shared-ikp/utils/date.utils';
import { Adresse } from '../../../../generated/model/adresse.model';
import { Kunde } from '../../../../generated/model/kunde.model';
import { Unternehmen } from '../../../../generated/model/unternehmen.model';
import { AdresseFormValue } from '../schemas';
import { isPrivatperson } from './beteiligte-partei.util';
import {
  PrivatpersonKontaktdatenFormValue,
  PrivatUnternehmenFormValue,
  RechnungsempfaengerFormValue,
  UnternehmenKontaktdatenFormValue,
  UnterschriftFormValue,
} from '../schemas/common-vertragspartner.schema';
import { AccountVertragspartnerFormValue, VerwalteterVertragspartnerFormValue } from '../schemas/neuer-vertragspartner.schema';

export function mapKundeToPrivatUnternehmenFormValue(bisherigerKunde: Kunde | null | undefined): PrivatUnternehmenFormValue {
  if (bisherigerKunde?.kundeTyp === Kunde.KundeTypEnum.UNBEKANNT) {
    return {
      vertragspartnerTyp: null,
      privatpersonKontaktdaten: null,
      unternehmenKontaktdaten: null,
      rechnungsempfaenger: initialNeuerRechnungsempfaengerFormValue(),
    };
  }

  const privatUnternehmen: PrivatUnternehmenFormValue = {
    vertragspartnerTyp: 'privat',
    privatpersonKontaktdaten: initalPrivatpersonKontaktdatenFormValue(),
    unternehmenKontaktdaten: initialUnternehmenKontaktdatenFormValue(),
    rechnungsempfaenger: initialNeuerRechnungsempfaengerFormValue(),
  };

  if (isPrivatperson(bisherigerKunde?.vertragspartner)) {
    const privatpersonKontaktdaten = privatUnternehmen.privatpersonKontaktdaten ?? initalPrivatpersonKontaktdatenFormValue();

    privatUnternehmen.vertragspartnerTyp = 'privat';
    privatpersonKontaktdaten.vorname = bisherigerKunde.vertragspartner.vorname ?? '';
    privatpersonKontaktdaten.nachname = bisherigerKunde.vertragspartner.nachname ?? '';
    privatpersonKontaktdaten.email = bisherigerKunde.email ?? '';
    privatpersonKontaktdaten.telefon = bisherigerKunde.telefon ?? '';
    privatpersonKontaktdaten.geburtsdatum = mapDateStringToNgbDateStruct(bisherigerKunde.vertragspartner.geburtsdatum);
    privatUnternehmen.privatpersonKontaktdaten = privatpersonKontaktdaten;
  } else if (bisherigerKunde?.vertragspartner) {
    const unternehmenKontaktdaten = privatUnternehmen.unternehmenKontaktdaten ?? initialUnternehmenKontaktdatenFormValue();

    privatUnternehmen.vertragspartnerTyp = 'unternehmen';
    unternehmenKontaktdaten.name = bisherigerKunde.vertragspartner.name ?? '';
    unternehmenKontaktdaten.geburtsdatum = mapDateStringToNgbDateStruct(bisherigerKunde.vertragspartner.geburtsdatum);
    unternehmenKontaktdaten.uid = bisherigerKunde.vertragspartner.uid ?? '';
    unternehmenKontaktdaten.firmenbuchnummer = bisherigerKunde.vertragspartner.firmenbuchnummer ?? '';
    unternehmenKontaktdaten.email = bisherigerKunde.email ?? '';
    unternehmenKontaktdaten.telefon = bisherigerKunde.telefon ?? '';
    privatUnternehmen.unternehmenKontaktdaten = unternehmenKontaktdaten;
  }

  return privatUnternehmen;
}

/**
 * Without `adresseFormValue` the Vertragspartner is sent without an address (step 4: the Zählpunkt
 * address is resolved by FE and BE as Rechnungsadresse instead).
 */
export function mapPrivatUnternehmenFormValueToKunde(
  privatUnternehmen: PrivatUnternehmenFormValue,
  adresseFormValue: AdresseFormValue | null,
): Kunde {
  const adresse = adresseFormValue ? mapAdresseFormValueToAdresse(adresseFormValue) : undefined;
  const rechnungsempfaenger = privatUnternehmen?.rechnungsempfaenger;
  const alternativeRechnungsempfaenger = rechnungsempfaenger?.hasAbweichendenRechnungsempfaenger
    ? mapRechnungsempfaengerFormValueToUnternehmen(rechnungsempfaenger)
    : null;

  if (privatUnternehmen?.vertragspartnerTyp === 'privat') {
    return {
      vertragspartner: {
        vorname: privatUnternehmen?.privatpersonKontaktdaten?.vorname.trim() ?? '',
        nachname: privatUnternehmen?.privatpersonKontaktdaten?.nachname.trim() ?? '',
        geburtsdatum: mapNgbDateStructToDateString(privatUnternehmen?.privatpersonKontaktdaten?.geburtsdatum ?? null),
        adresse,
      },
      kundeTyp: Kunde.KundeTypEnum.EXTERNER_KUNDE,
      email: privatUnternehmen?.privatpersonKontaktdaten?.email.trim() ?? '',
      telefon: privatUnternehmen?.privatpersonKontaktdaten?.telefon.trim() ?? '',
      rechnungsempfaenger: alternativeRechnungsempfaenger,
    };
  }

  return {
    kundeTyp: Kunde.KundeTypEnum.EXTERNER_KUNDE,
    vertragspartner: {
      name: privatUnternehmen?.unternehmenKontaktdaten?.name.trim() ?? '',
      kundennummer: null,
      firmenbuchnummer: privatUnternehmen?.unternehmenKontaktdaten?.firmenbuchnummer.trim() ?? '',
      uid: privatUnternehmen?.unternehmenKontaktdaten?.uid.trim() ?? '',
      geburtsdatum: mapNgbDateStructToDateString(privatUnternehmen?.unternehmenKontaktdaten?.geburtsdatum ?? null),
      adresse,
    },
    email: privatUnternehmen?.unternehmenKontaktdaten?.email.trim() ?? '',
    telefon: privatUnternehmen?.unternehmenKontaktdaten?.telefon.trim() ?? '',
    rechnungsempfaenger: alternativeRechnungsempfaenger,
  };
}

export function mapRechnungsempfaengerFormValueToUnternehmen(rechnungsempfaenger: RechnungsempfaengerFormValue): Unternehmen {
  return {
    name: rechnungsempfaenger.name.trim(),
    kundennummer: rechnungsempfaenger.kundennummer.trim(),
    uid: rechnungsempfaenger.uid.trim(),
    firmenbuchnummer: rechnungsempfaenger.firmenbuchnummer.trim(),
    adresse: mapAdresseFormValueToAdresse(rechnungsempfaenger.adresse),
  };
}

export function mapAbweichenderRechnungsempfaengerToUnternehmen(rechnungsempfaenger: RechnungsempfaengerFormValue): Unternehmen | null {
  if (!rechnungsempfaenger.hasAbweichendenRechnungsempfaenger) {
    return null;
  }

  return {
    name: rechnungsempfaenger.name.trim(),
    adresse: mapAdresseFormValueToAdresse(rechnungsempfaenger.adresse),
  };
}

export function initalPrivatpersonKontaktdatenFormValue(): PrivatpersonKontaktdatenFormValue {
  return {
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    geburtsdatum: null,
  };
}

export function initialUnternehmenKontaktdatenFormValue(): UnternehmenKontaktdatenFormValue {
  return {
    name: '',
    geburtsdatum: null,
    uid: '',
    firmenbuchnummer: '',
    email: '',
    telefon: '',
  };
}

export function initialNeuerRechnungsempfaengerFormValue(): RechnungsempfaengerFormValue {
  return {
    hasAbweichendenRechnungsempfaenger: false,
    name: '',
    kundennummer: '',
    adresse: mapAdresseToFormValue(null),
    uid: '',
    firmenbuchnummer: '',
  };
}

export function initialVerwalteterVertragspartnerFormValue(): VerwalteterVertragspartnerFormValue {
  return {
    name: null,
    kundennummer: null,
    firmenbuchnummer: null,
    uid: null,
    adresse: mapAdresseToFormValue(null),
    email: null,
    telefon: null,
    rechnungsempfaenger: initialNeuerRechnungsempfaengerFormValue(),
  };
}

export function initialAccountVertragspartnerFormValue(): AccountVertragspartnerFormValue {
  return {
    name: '',
    kundennummer: '',
    adresse: mapAdresseToFormValue(null),
    uid: '',
    firmenbuchnummer: '',
    rechnungsempfaenger: initialNeuerRechnungsempfaengerFormValue(),
  };
}

export function mapAdresseFormValueToAdresse(formValue?: AdresseFormValue): Adresse {
  return {
    plz: formValue?.plz?.trim(),
    ort: formValue?.ort?.trim(),
    strasse: formValue?.strasse?.trim(),
    hausnummer: formValue?.hausnummer?.trim(),
    stiege: formValue?.stiege?.trim(),
    tuernummer: formValue?.tuernummer?.trim(),
  };
}

export function mapAdresseToFormValue(adresse: Adresse | null | undefined): AdresseFormValue {
  return {
    plz: adresse?.plz ?? '',
    ort: adresse?.ort ?? '',
    strasse: adresse?.strasse ?? '',
    hausnummer: adresse?.hausnummer ?? '',
    stiege: adresse?.stiege ?? '',
    tuernummer: adresse?.tuernummer ?? '',
  };
}

export function initialAdresseFormValue(): AdresseFormValue {
  return mapAdresseToFormValue(null);
}

export function mapKundeToUnterschriftFormValue(kunde: Kunde | null | undefined): UnterschriftFormValue {
  return {
    unterschrift: kunde?.unterschrift ?? null,
    hasZustimmung: kunde?.hasZustimmung ?? false,
  };
}

export function withUnterschrift(kunde: Kunde, draft: UnterschriftFormValue): Kunde {
  return {
    ...kunde,
    unterschrift: draft.unterschrift ?? undefined,
    hasZustimmung: draft.hasZustimmung,
  };
}
